import React, { useState } from "react";
import {
    DropdownServices,
    FiltersButton,
    InfoBanner,
    Spinner,
    TableHeader,
    WithRoleAccess,
    WithModulesAccess,
} from "@services/ui-components";
import { Button, Col, Row } from "react-bootstrap";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { useGetServiceQuery } from "@me-team/host/main/technologicalMaps/graphql/technologicalMaps.hooks";
import ErrorService from "../../../services/ErrorService";
import { TechMapsFilterI } from "../types";
import { AllModulesTagName } from "../../../utils/enums";
import TechMapsListForServiceFilterWrapper from "./TechMapsListForServiceFilterWrapper";
import TechMapsListForServiceTable from "./TechMapsListForServiceTable";
import CreateTechnologicalMap from "../CreateTechnologicalMap/CreateTechnologicalMap";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../../hooks/useToast";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

const TechMapsListForService: React.FC = () => {
    const { t } = useTranslation();
    const { serviceId, branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canCreateTechMap = useRoleAccess(CompanyAccess.TechMapCreate);
    const canCreateTechMapCategory = useRoleAccess(CompanyAccess.TechMapCategoryCreate);
    const [showFilters, setShowFilters] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [showCreateTechMapModal, setShowCreateTechMapModal] = useState(false);
    const [refetchTechMaps, setRefetchTechMaps] = useState(null);
    const { showToast, toastText, toggleShowToast, setShowToast } = useToast();

    const handleSetRefetch = (refetchTechMaps: any) => {
        setRefetchTechMaps(() => refetchTechMaps);
    };

    const { data: oneServiceData, loading: oneServiceLoading } = useGetServiceQuery({
        variables: {
            id: +serviceId,
            companyId: +currentCompanyId,
        },
        skip: !currentCompanyId,
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const serviceName = oneServiceData?.user?.company?.services?.services?.[0]?.name;

    const initialValueFilter: TechMapsFilterI = {
        search: null,
        categoryId: null,
    };

    const [filters, setFilters] = useState<TechMapsFilterI>(initialValueFilter);

    const toggleFilters = () => setShowFilters(!showFilters);
    const handleCreateTechMap = () => {
        if (!canCreateTechMap) return;

        setShowCreateTechMapModal((prevState) => !prevState);
    };

    if (oneServiceLoading || !currentCompanyId) return <Spinner />;
    return (
        <div className="mb-9 px-2 px-lg-0">
            <InfoBanner>
                <p className="fs-7 mb-0">
                    {t(
                        "Select the services for which you want to use the technological card is to be used (the products from the technological card will be offered for write-off after the service is completed)."
                    )}
                </p>
            </InfoBanner>
            <TableHeader
                title={`${t("Technological Maps Services")} ${serviceName}`}
                paths={[
                    {
                        path: `/services/service/${currentCompanyId}/service-list/${serviceId}/technological/maps/branch/${branchId}`,
                        label: t("Services"),
                    },
                    {
                        path: `/services/service/${currentCompanyId}/service-list/${branchId}`,
                        label: t("Service list"),
                    },
                    {
                        path: `/services/service/${currentCompanyId}/service-list/${serviceId}/technological/maps/branch/${branchId}`,
                        label: `${t("Technological Maps Services")} ${serviceName}`,
                    },
                ]}
                colSize={5}
                btnColSize={7}
                buttons={
                    <Row className="gx-3 justify-content-end align-items-center mt-4 mt-lg-0">
                        <WithModulesAccess modules={[AllModulesTagName.SERVICE_PROVIDES, AllModulesTagName.PERSONNEL]}>
                            <WithRoleAccess permission={CompanyAccess.ServiceEmployeesView}>
                                <Col xs={12} sm={6} lg={6} xxl={3} className="mb-3 mb-xxl-0">
                                    <Link
                                        to={`/services-providers/${serviceId}/${branchId}`}
                                        className="btn btn-orange px-1 w-100 text-truncate text-capitalize fw-bold"
                                    >
                                        {t("To Providers")}
                                    </Link>
                                </Col>
                            </WithRoleAccess>
                        </WithModulesAccess>
                        <WithRoleAccess permission={CompanyAccess.ServiceListView}>
                            <Col xs={12} sm={6} lg={5} xxl={3} className="mb-3 mb-xxl-0">
                                <DropdownServices
                                    currentCompanyId={currentCompanyId}
                                    serviceId={serviceId}
                                    branchId={branchId}
                                />
                            </Col>
                        </WithRoleAccess>
                        <WithRoleAccess permission={CompanyAccess.TechMapCreate}>
                            <Col xs={12} sm={6} lg={7} xxl={4}>
                                <Button
                                    variant="primary"
                                    className="w-100 text-white mb-3 mb-lg-0 text-truncate"
                                    onClick={handleCreateTechMap}
                                >
                                    <i className="bi bi-plus-lg fw-bold me-1 w-100 h-100"></i>
                                    {t("Add Technological Map")}
                                </Button>
                            </Col>
                        </WithRoleAccess>
                        <Col xs={12} sm={6} lg={4} xxl={2}>
                            <FiltersButton
                                filter={filters}
                                showFilters={showFilters}
                                onClick={toggleFilters}
                                classes="mb-3 mb-lg-0"
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
            >
                <TechMapsListForServiceFilterWrapper setFilters={setFilters} />
            </TableHeader>
            <TechMapsListForServiceTable
                filters={filters}
                setRefetchTechMaps={handleSetRefetch}
                onShowCreateTechMapModal={handleCreateTechMap}
                toggleShowToast={toggleShowToast}
            />

            {canCreateTechMap && showCreateTechMapModal && (
                <CreateTechnologicalMap
                    show={showCreateTechMapModal}
                    onClose={handleCreateTechMap}
                    refetchTechMaps={refetchTechMaps}
                    toggleShowToast={toggleShowToast}
                    canCreateTechMap={canCreateTechMap}
                    canCreateTechMapCategory={canCreateTechMapCategory}
                />
            )}

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </div>
    );
};

export default TechMapsListForService;
